import { useDispatch } from "react-redux";
import { logInUser, signUpUser, confirmCode, resendConfirmation } from "./api";
import {
  VStack,
  Button,
  Box,
  useToast,
  Tooltip,
  Divider,
  Text,
} from "@chakra-ui/react";

import { Formik } from "formik";
import * as Yup from "yup";
import TextInput from "../components/forms/TextInput";
import PasswordInput from "../components/forms/PasswordInput";
import SignUpFooter from "./SignUpFooter";
import TurnstileWidget from "../components/forms/TurnstileWidget";
import * as N from "../utilities/notifications";
import { getUserObject } from "../redux/actions/user";
import { useRouter } from "next/router";
import { useRef, useState } from "react";
import { FaChevronLeft } from "react-icons/fa6";
import { Button as HeadlessButton } from "@headlessui/react";
import { getErrorMessage } from "../utilities/helpers";

const CAPTCHA_ERROR_MESSAGE = "CAPTCHA verification failed. Please try again.";
const REGISTRATION_UNAVAILABLE_MESSAGE =
  "Registration is temporarily unavailable. Please try again later.";

const SignUpClientForm = ({
  noBorder = false,
  emailValidation = "true",
  noRedirect = false,
  loginCallback = null,
}) => {
  const dispatch = useDispatch();
  const toast = useToast();
  const router = useRouter();

  const [verifyEmail, setVerifyEmail] = useState(false);
  const [verificationEmail, setVerificationEmail] = useState("");
  const [captchaToken, setCaptchaToken] = useState(null);
  const turnstileRef = useRef(null);

  // Turnstile tokens are single-use; force a fresh one for the next attempt.
  const resetCaptcha = () => {
    setCaptchaToken(null);
    turnstileRef.current?.reset();
  };

  const emailCriteria = "Email cannot be from welldoing.org";
  const passwordCriteria =
    "Password must be at least 8 characters long and include an uppercase & lowercase letter, a number and a symbol such as !@£$%^*";

  const handleVerifyEmail = async (data, setSubmitting) => {
    try {
      await dispatch(confirmCode(verificationEmail, data.verificationCode));
      if (!data.password) {
        toast(N.genericSuccess("Email verified", "Please log in to continue."));
        setVerifyEmail(false);
        if (loginCallback) {
          loginCallback();
        }
        setSubmitting(false);
        return;
      }
      toast(N.verifyEmailSuccess);
      // Attempt login again after verification
      await handleSubmit(
        { email: verificationEmail, password: data.password },
        setSubmitting
      );
      setVerifyEmail(false);
    } catch (e) {
      toast(
        N.genericFail(
          "Verification failed",
          e.message || "The verification code is incorrect."
        )
      );
    }
   finally {
    setSubmitting(false);
   }
  };

  const handleResendCode = async () => {
    try {
      await resendConfirmation(verificationEmail);
      toast(N.resendCodeSuccess);
    } catch (e) {
      toast(N.genericFail("Failed to resend code", getErrorMessage(e)));
    }
  };

  const handleSubmit = async (data, setSubmitting) => {
    try {
      if (!verifyEmail) {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: "form_submit",
          formId: "client-form",
        });

        await dispatch(signUpUser(data));
      }

      setSubmitting(false);
      let token = await logInUser({
        email: data.email,
        password: data.password,
      });

      await dispatch(getUserObject(token));

      if (noRedirect) {
        return true;
      }

      router.push(
        "/accounts/user-account/dashboard?success=true&registration=client"
      );
    } catch (err) {
      setSubmitting(false);
      if (err === "userNotConfirmed") {
        setVerificationEmail(data.email);
        setVerifyEmail(true);
        return;
      }

      // Any failed sign-up consumes the Turnstile token, so reset the widget.
      resetCaptcha();

      const errorMessage = typeof err === "string" ? err : getErrorMessage(err);
      if (
        errorMessage === CAPTCHA_ERROR_MESSAGE ||
        errorMessage === REGISTRATION_UNAVAILABLE_MESSAGE
      ) {
        toast(N.signupFail(errorMessage));
        return true;
      }

      toast(
        N.signupFail(
          `There was a problem signing up. ${err} Please check your details and try again.`
        )
      );

      return true;
    }
  };

  return (
    <>
      <Formik
        id="client-form"
        initialValues={{
          fullname: "",
          email: "",
          confirmEmail: "",
          password: "",
          confirmPassword: "",
          type: "Client",
          subType: null,
          verificationCode: "",
        }}
        validateOnBlur={false}
        validationSchema={Yup.object({
          fullname: verifyEmail ? Yup.string() : Yup.string().required("Full name required"),
          email: verifyEmail ? Yup.string() : Yup.string()
            .email()
            .required("Email required")
            .test("no-welldoing-domain", emailCriteria, (value) => {
              if (!value) return true;
              const domain = value.trim().split("@")[1]?.toLowerCase();
              return !domain?.endsWith("welldoing.org");
            }),
          confirmEmail: verifyEmail ? Yup.string() : Yup.string()
            .email()
            .required("Please confirm your email")
            .test("no-welldoing-domain-confirm", emailCriteria, (value) => {
              if (!value) return true;
              const domain = value.trim().split("@")[1]?.toLowerCase();
              return !domain?.endsWith("welldoing.org");
            })
            .oneOf([Yup.ref("email")], "Email does not match"),

          password: Yup.string()
            .required("Password required")
            .min(8, passwordCriteria)
            .matches(
              /^(?=.{8,}$)(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*\W).*$/,
              passwordCriteria
            ),
          confirmPassword: verifyEmail ? Yup.string() : Yup.string()
            .required("Please confirm your password")
            .min(8, passwordCriteria)
            .matches(
              /^(?=.{8,}$)(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*\W).*$/,
              passwordCriteria
            )
            .oneOf([Yup.ref("password")], "Your passwords do not match"),
          verificationCode: verifyEmail
            ? Yup.string().required("Verification code required")
            : Yup.string(),
        })}
        onSubmit={(values, { setSubmitting }) => {
          if (verifyEmail) {
            handleVerifyEmail(values, setSubmitting);
          } else {
            const { email, password } = values;

            // Create metadata object
            let metadata = { ...values };
            delete metadata.password;
            delete metadata.confirmPassword;
            metadata.emailValidation = emailValidation;
            // Backend (Cognito pre-sign-up Lambda) requires the Turnstile token
            // in clientMetadata under `captchaToken`.
            metadata.captchaToken = captchaToken;

            // Create data object
            const data = {
              email,
              password,
              metadata,
            };

            handleSubmit(data, setSubmitting);
          }
        }}
      >
        {(formik) => (
          <VStack
            as="form"
            maxW="full"
            py={noBorder ? 2 : 7}
            px={noBorder ? 1 : 5}
            mx="auto"
            spacing={4}
            alignItems="flex-start"
            bg="white"
            borderRadius={{ base: 0, lg: 20 }}
            onSubmit={formik.handleSubmit}
            borderColor="brand.200"
            borderWidth={noBorder ? 0 : { base: 0, lg: 0.5 }}
            overflow="hidden"
            position="relative"
            id="client-registration-form"
          >
            {verifyEmail ? (
              <>
                <HeadlessButton
                  className="flex gap-2 items-center text-sm text-brand-alt"
                  onClick={() => setVerifyEmail(false)}
                >
                  <FaChevronLeft className="h-3 w-3" />
                  <span>Back to signup</span>
                </HeadlessButton>
                <Text>
                  Please enter the verification code sent to{" "}
                  <b>{verificationEmail}</b>.
                </Text>
                <TextInput
                  name="verificationCode"
                  formlabel="Verification code"
                  type="text"
                  placeholder="123456"
                />
                <Button
                  variant="link"
                  fontSize="sm"
                  bgGradient="linear(to-r, primary.600, primary.500)"
                  bgClip="text"
                  onClick={handleResendCode}
                >
                  Resend verification code
                </Button>
              </>
            ) : (
              <>
                <TextInput
                  name="fullname"
                  formlabel="Full name"
                  type="text"
                  placeholder="Robin"
                />
                <TextInput
                  name="email"
                  formlabel="Email"
                  type="email"
                  placeholder="e.g. robin@yourmail.com"
                />
                <TextInput
                  name="confirmEmail"
                  formlabel="Confirm email"
                  type="email"
                  placeholder="e.g. robin@yourmail.com"
                />
                <Box
                  display={{ base: "block", md: "flex" }}
                  w="100%"
                  justifyContent="space-between"
                >
                  <Tooltip
                    hasArrow
                    label={passwordCriteria}
                    bg="primary.200"
                    color="brand.300"
                    placement="bottom"
                  >
                    <Box w={{ base: "100%", md: "48%" }}>
                      <PasswordInput
                        name="password"
                        placeholder="8+characters"
                        formlabel="Password"
                      />
                    </Box>
                  </Tooltip>
                  <Box w={{ base: "100%", md: "48%" }} mt={[4, 4, 0]}>
                    <PasswordInput
                      name="confirmPassword"
                      formlabel="Confirm password"
                      placeholder="Re-enter password"
                    />
                  </Box>
                </Box>
              </>
            )}
            {!verifyEmail && (
              <Box w="100%">
                <TurnstileWidget
                  ref={turnstileRef}
                  onVerify={(token) => setCaptchaToken(token)}
                  onExpire={() => setCaptchaToken(null)}
                  onError={() => setCaptchaToken(null)}
                />
              </Box>
            )}
            <Box p="1rem 0 0 0" w="100%">
              <Button
                w="full"
                variant="solid"
                type="submit"
                isLoading={formik.isSubmitting}
                isDisabled={
                  Object.keys(formik.errors).length > 0 ||
                  (!verifyEmail && !captchaToken)
                }
                loadingText="Submitting"
              >
                {verifyEmail ? "Verify email" : "Sign up"}
              </Button>
            </Box>
            {!verifyEmail && (
              <>
                <div className="w-full flex justify-center items-center gap-4">
                  <div className="flex-1 border-b border-b-brand-off" />
                  <span className="text-sm text-brand-alt py-2">Or</span>
                  <div className="flex-1 border-b border-b-brand-off" />
                </div>
                <div className="w-full flex justify-center items-center">
                  <Button
                    className="border bg-white border-brand rounded-full text-brand hover:text-white hover:bg-brand transition px-4 py-2"
                    onClick={loginCallback}
                  >
                    Log into existing account
                  </Button>
                </div>
              </>
            )}
            <SignUpFooter />
          </VStack>
        )}
      </Formik>
    </>
  );
};

export default SignUpClientForm;
